import { App as AntApp, Button, Checkbox, Modal } from "antd";
import axios from "axios";
import { CopyPlusIcon, PencilIcon } from "lucide-react";
import { useState, type ReactNode } from "react";
import { useNavigate } from "react-router";
import { useDuplicatePortalItineraryMutation } from "../actions/itinerary.actions";
import { getPortalPaths } from "../lib/routes";

type EditChoice = "copy" | "same";

const STORAGE_KEY = "agentPortal.editQuoteChoice";

const readRememberedChoice = (): EditChoice | null => {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return value === "copy" || value === "same" ? value : null;
  } catch {
    return null;
  }
};

const rememberChoice = (choice: EditChoice) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, choice);
  } catch {
    // Storage can be unavailable (private windows); the choice is simply asked again next time.
  }
};

/**
 * Starts editing a saved quote. Customers sometimes confirm an earlier option, so the agent chooses
 * between editing the same quote or a copy; "Remember my choice" skips the question next time.
 * Render the returned `modalHolder` once in the page.
 */
export const useEditQuote = () => {
  const navigate = useNavigate();
  const { message } = AntApp.useApp();
  const paths = getPortalPaths();
  const duplicateMutation = useDuplicatePortalItineraryMutation();
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [remember, setRemember] = useState(false);

  const apply = async (itineraryId: string, choice: EditChoice) => {
    if (choice === "same") {
      navigate(paths.editItinerary(itineraryId));
      return;
    }
    try {
      const copy = await duplicateMutation.mutateAsync(itineraryId);
      message.success("Copy created. You are editing the copy.");
      navigate(paths.editItinerary(copy.id));
    } catch (error) {
      message.error(
        axios.isAxiosError<{ error?: string }>(error)
          ? error.response?.data?.error || "Unable to create a copy of this quote"
          : "Unable to create a copy of this quote",
      );
    }
  };

  /** Edits the quote straight away when a choice is remembered, otherwise asks first. */
  const startEdit = (itineraryId: string) => {
    const remembered = readRememberedChoice();
    if (remembered) void apply(itineraryId, remembered);
    else {
      setRemember(false);
      setPendingId(itineraryId);
    }
  };

  const choose = async (choice: EditChoice) => {
    if (!pendingId) return;
    if (remember) rememberChoice(choice);
    const itineraryId = pendingId;
    setPendingId(null);
    await apply(itineraryId, choice);
  };

  const modalHolder: ReactNode = (
    <Modal
      open={Boolean(pendingId)}
      onCancel={() => setPendingId(null)}
      title="Edit this quote"
      footer={null}
      width={460}
    >
      <p className="text-sm leading-6 text-gray-600">
        A customer may still confirm the earlier quote. Create a copy to keep it unchanged, or edit it in place.
      </p>
      <div className="mt-4 flex flex-col gap-2">
        <Button type="primary" icon={<CopyPlusIcon size={15} />} loading={duplicateMutation.isPending} onClick={() => void choose("copy")}>
          Create a copy and edit
        </Button>
        <Button icon={<PencilIcon size={15} />} onClick={() => void choose("same")}>
          Edit the same quote
        </Button>
      </div>
      <Checkbox className="mt-4" checked={remember} onChange={(event) => setRemember(event.target.checked)}>
        Remember my choice
      </Checkbox>
    </Modal>
  );

  return { startEdit, modalHolder };
};
